/* eslint-disable no-console */
import { defineNuxtPlugin } from '@nuxtjs/composition-api';
import type { ExtendedError, pushError } from './types';
import { isLocalstorageAvailable, willSendToSentry } from './utils';
import type { Auth } from '~/types/plugins';

const isExtendedError = (error: unknown): error is ExtendedError => {
  return error instanceof Error;
};

export default defineNuxtPlugin(({ app }, inject) => {
  if (app.$sentry) {
    // Set user info for sentry
    const setUser = (auth: Auth) => {
      const user = auth?.user;
      const person = user?.id ? { id: user.id, email: user.email, username: user.autoName } : null;

      if (person) {
        app.$sentry.setUser(person);
      }
    };
    app.store?.watch(store => store.auth, setUser, { immediate: true });

    // Set context tag
    app.$sentry.setTag('context', process.client ? 'client' : 'server');
  }

  const pushError: pushError = (error, data) => {
    if (!isExtendedError(error)) return false;
    const statusCode = error.response?.status || error.status;
    const message = error.response?.statusText || error.statusText || error.message;
    let fullstoryLink: string | null | undefined;

    if (typeof data === 'string') {
      // eslint-disable-next-line
      data = {
        info: data,
      };
    }

    if (process.client) {
      try {
        fullstoryLink = window.FS?.getCurrentSessionURL();
      } catch (e) {}
    }

    if (app.$sentry) {
      if (willSendToSentry(error)) {
        app.$sentry.captureException(error, {
          extra: {
            statusCode,
            message,
            // Debug information for WebView
            ...(process.client && {
              hasSupport: {
                cookies: navigator.cookieEnabled,
                localStorage: isLocalstorageAvailable,
              },
            }),
            ...(fullstoryLink && { fullstoryLink }),
            ...data,
          },
        });
      }
    } else {
      console.error(error);
    }

    return { statusCode, message };
  };

  inject('sentryUtils', {
    error: pushError,
  });
});
